import { useState } from "react";
import { STAGES, type AuditFile } from "./india";
import { AuditIcon, type AuditModule } from "./AuditPlatformShell";

const reviewed = (f: AuditFile) =>
  f.procedures.filter((p) => p.status === "reviewed").length;
const percent = (f: AuditFile) =>
  Math.round((reviewed(f) / Math.max(1, f.procedures.length)) * 100);
export function AuditPortfolio({
  files,
  register,
  onCreate,
  onOpen,
  onModule,
}: {
  files: AuditFile[];
  register: boolean;
  onCreate: () => void;
  onOpen: (
    id: string,
    tab: "Overview" | "Working papers" | "Queries" | "Findings",
  ) => void;
  onModule: (module: AuditModule) => void;
}) {
  const [search, setSearch] = useState("");
  const [stage, setStage] = useState("All stages");
  const [scope, setScope] = useState("active");
  const active = files.filter((f) => f.stage !== "Archived");
  const prepared = active
    .flatMap((f) => f.procedures)
    .filter((p) => p.status === "prepared").length;
  const openQueries = active
    .flatMap((f) => f.queries)
    .filter((q) => q.status !== "closed").length;
  const openFindings = active
    .flatMap((f) => f.findings)
    .filter((f) => f.disposition === "open").length;
  const filtered = files.filter(
    (f) =>
      (scope === "all" ||
        (scope === "archived"
          ? f.stage === "Archived"
          : f.stage !== "Archived")) &&
      (stage === "All stages" || stage === f.stage) &&
      `${f.clientName} ${f.partner} ${f.periodEnd}`
        .toLowerCase()
        .includes(search.trim().toLowerCase()),
  );
  const pending = active.filter(
    (f) =>
      f.procedures.some((p) => p.status === "prepared") ||
      f.findings.some((v) => v.disposition === "open") ||
      f.queries.some((q) => q.status !== "closed"),
  );
  const total = active.reduce((n, f) => n + f.procedures.length, 0);
  const complete = active.reduce((n, f) => n + reviewed(f), 0);
  return (
    <>
      <div className="afb-page-heading">
        <div>
          <p className="afb-eyebrow">
            {register ? "WORKING PAPERS" : "YOUR AUDIT PORTFOLIO"}
          </p>
          <h1>{register ? "Engagements" : "Dashboard"}</h1>
          <p>
            {register
              ? "One place for every engagement, workpaper and review."
              : "A clear view of your engagements and the work that needs attention."}
          </p>
        </div>
        <button className="afi-primary" onClick={onCreate}>
          + New engagement
        </button>
      </div>
      <div className="afb-metrics">
        {[
          {
            label: "Active engagements",
            value: active.length,
            note: "Across your browser drafts",
            icon: "folder",
            action: () => {
              setScope("active");
              setStage("All stages");
              setSearch("");
            },
          },
          {
            label: "Awaiting review",
            value: prepared,
            note: "Prepared procedures",
            icon: "shield",
            action: () => onModule("quality"),
          },
          {
            label: "Open draft queries",
            value: openQueries,
            note: "Shared queries in Collaborate",
            icon: "message",
            action: () => {
              const f = active.find((f) =>
                f.queries.some((q) => q.status !== "closed"),
              );
              f ? onOpen(f.id, "Queries") : onModule("collaboration");
            },
          },
          {
            label: "Open findings",
            value: openFindings,
            note: "Require a disposition",
            icon: "chart",
            action: () => {
              const f = active.find((f) =>
                f.findings.some((x) => x.disposition === "open"),
              );
              f ? onOpen(f.id, "Findings") : onModule("quality");
            },
          },
        ].map((item) => (
          <button
            className="afb-metric afb-metric-button"
            key={item.label}
            onClick={item.action}
          >
            <span className="afb-metric-top">
              {item.label}
              <AuditIcon name={item.icon} />
            </span>
            <strong>{item.value}</strong>
            <small>
              {item.note}
              <span aria-hidden="true">↗</span>
            </small>
          </button>
        ))}
      </div>
      {!register && (
        <div className="afb-dashboard-panels">
          <section className="afb-panel">
            <div className="afb-panel-heading">
              <h2>Engagement progress</h2>
              <span className="afb-badge">{active.length} active</span>
            </div>
            <div className="afb-stage-chart">
              {STAGES.filter((s) => s !== "Archived").map((s) => {
                const count = active.filter((f) => f.stage === s).length;
                return (
                  <button
                    key={s}
                    onClick={() => {
                      setScope("active");
                      setStage(s);
                      setSearch("");
                    }}
                    aria-label={`Filter ${s} engagements`}
                  >
                    <span>{s}</span>
                    <span className="afb-stage-track">
                      <span
                        style={{
                          width: `${active.length ? (count / active.length) * 100 : 0}%`,
                        }}
                      />
                    </span>
                    <strong>{count}</strong>
                  </button>
                );
              })}
            </div>
          </section>
          <section className="afb-panel afb-review-summary">
            <div className="afb-panel-heading">
              <h2>Review coverage</h2>
              <AuditIcon name="shield" />
            </div>
            <div className="afb-review-number">
              {total ? Math.round((complete / total) * 100) : 0}
              <span>%</span>
            </div>
            <p>
              {complete} of {total} procedures reviewed
            </p>
            <progress
              aria-label="Portfolio review coverage"
              max={Math.max(total, 1)}
              value={complete}
            />
            <button className="afi-link" onClick={() => onModule("quality")}>
              Open review overview <span aria-hidden="true">→</span>
            </button>
          </section>
        </div>
      )}
      <section className="afb-panel afb-register">
        <div className="afb-panel-heading">
          <div>
            <h2>{register ? "Engagement register" : "Your engagements"}</h2>
            <p>Local audit drafts · {filtered.length} shown</p>
          </div>
          <div className="afb-segmented" aria-label="Engagement scope">
            {[
              ["active", "Active"],
              ["archived", "Archived"],
              ["all", "All"],
            ].map(([key, label]) => (
              <button
                key={key}
                aria-pressed={scope === key}
                onClick={() => {
                  setScope(key);
                  setStage("All stages");
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="afb-toolbar">
          <input
            aria-label="Search engagements"
            placeholder="Search client, partner or period…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            aria-label="Filter engagement stage"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
          >
            <option>All stages</option>
            {STAGES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
        {filtered.length ? (
          <div className="afb-table-wrap">
            <table className="afb-table">
              <thead>
                <tr>
                  <th>Engagement</th>
                  <th>Period end</th>
                  <th>Framework</th>
                  <th>Stage</th>
                  <th>Review progress</th>
                  <th>Partner</th>
                  <th>
                    <span className="afb-sr-only">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <button
                        className="afi-link"
                        onClick={() => onOpen(f.id, "Overview")}
                      >
                        {f.clientName}
                      </button>
                      <small>
                        {f.consolidated ? "Consolidated" : "Standalone"} audit
                      </small>
                    </td>
                    <td>{f.periodEnd}</td>
                    <td>
                      <span className="afb-badge">{f.framework}</span>
                    </td>
                    <td>
                      <span
                        className={`afb-status afb-status-${f.stage.toLowerCase()}`}
                      >
                        {f.stage}
                      </span>
                    </td>
                    <td>
                      <div className="afb-table-progress">
                        <progress
                          aria-label={`${f.clientName} review progress`}
                          max={100}
                          value={percent(f)}
                        />
                        <span>{percent(f)}%</span>
                      </div>
                    </td>
                    <td>{f.partner}</td>
                    <td>
                      <button
                        className="afb-open-button"
                        aria-label={`Open ${f.clientName}`}
                        onClick={() => onOpen(f.id, "Overview")}
                      >
                        →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="afb-empty">
            <span className="afb-empty-icon">
              <AuditIcon name="folder" />
            </span>
            <h3>
              {files.length
                ? "No engagements match these filters"
                : "Your next audit starts here"}
            </h3>
            <p>
              {files.length
                ? "Change the search or filters to find an engagement."
                : "Create an engagement to organise planning, trial balances, risks and working papers."}
            </p>
            <button
              onClick={
                files.length
                  ? () => {
                      setSearch("");
                      setStage("All stages");
                      setScope("all");
                    }
                  : onCreate
              }
            >
              {files.length ? "Clear filters" : "Create your first engagement"}
            </button>
          </div>
        )}
      </section>
      {!register && (
        <div className="afb-two-column">
          <section className="afb-panel">
            <div className="afb-panel-heading">
              <h2>Needs attention</h2>
              <span className="afb-badge">{pending.length} engagements</span>
            </div>
            {pending.length ? (
              pending.slice(0, 5).map((f) => (
                <div className="afb-attention-item" key={f.id}>
                  <span className="afb-attention-dot" />
                  <div>
                    <button
                      className="afi-link"
                      aria-label={`Follow up on ${f.clientName}`}
                      onClick={() => onOpen(f.id, "Overview")}
                    >
                      {f.clientName}
                    </button>
                    <small>
                      {
                        f.procedures.filter((p) => p.status === "prepared")
                          .length
                      }{" "}
                      awaiting review ·{" "}
                      {f.queries.filter((q) => q.status !== "closed").length}{" "}
                      open queries ·{" "}
                      {
                        f.findings.filter((x) => x.disposition === "open")
                          .length
                      }{" "}
                      open findings
                    </small>
                  </div>
                  <button
                    className="afb-open-button"
                    onClick={() => onOpen(f.id, "Overview")}
                    aria-label={`Review ${f.clientName}`}
                  >
                    →
                  </button>
                </div>
              ))
            ) : (
              <p className="afb-calm-empty">
                No prepared procedures, open draft queries or findings to follow
                up.
              </p>
            )}
          </section>
          <section className="afb-panel">
            <div className="afb-panel-heading">
              <h2>Connected audit tools</h2>
            </div>
            {[
              {
                key: "analytics" as const,
                title: "Audit data analytics",
                text: "Explore trial balances and account movements",
                icon: "chart",
              },
              {
                key: "collaboration" as const,
                title: "Digital collaboration",
                text: "Shared queries, client responses and evidence",
                icon: "message",
              },
              {
                key: "quality" as const,
                title: "Quality management",
                text: "Review coverage and engagement readiness",
                icon: "shield",
              },
            ].map((x) => (
              <button
                className="afb-tool-row"
                key={x.key}
                onClick={() => onModule(x.key)}
              >
                <span className="afb-tool-icon">
                  <AuditIcon name={x.icon} />
                </span>
                <span>
                  <strong>{x.title}</strong>
                  <small>{x.text}</small>
                </span>
                <span aria-hidden="true">→</span>
              </button>
            ))}
          </section>
        </div>
      )}
    </>
  );
}
